import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter as FilterIcon, Plus, X } from 'lucide-react';
import SavedFilterTabs from '../../shared/SavedFilterTabs';
import FilterDrawer from '../../shared/FilterDrawer';
import FilterBanner from '../../shared/FilterBanner';
import CancelButton from '../../shared/CancelButton';
import SaveButton from '../../shared/SaveButton';
import RowActionsMenu from '../../shared/RowActionsMenu';
import ReportOverlay from '../../shared/ReportOverlay';
import PackingListReportView from './PackingListReportView';
import { usePackingStation } from './packingStation';
import ShipConfirmFields, { blankShipFields } from './ShipConfirmFields';
import { shipPayload } from './ShipConfirmDialog';
import { shippablePackages, insideSummary, actualPostageOf, shipmentShippingCharge } from './packageShared';
import ShipmentEditDrawer from './ShipmentEditDrawer';
import ShipmentReverseDialog from './ShipmentReverseDialog';
import { carriageLabel } from './ShipmentCarriageFields';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../shared/pagination';
import '../../shared/ListRowHover.css';
import '../../shared/StyledInput.css';
import { todayInTenant, formatDate, formatDateTime } from '../../shared/tenantClock';
import DateInput from '../../shared/DateInput';
import { formatNumber, formatMoney } from '../../shared/numberFormat';
import { useScreenAccess } from '../../shared/PermissionsContext';
import { useShipmentWeightUnit } from '../../shared/useShipmentWeightUnit';
import { askConfirm, showMessage } from '../../shared/AppDialogs';

// Cross-order Shipments list (see vis_order_fulfilment.md) — every shipment
// (tracking number, shipped date, its packages) across every
// order of every type (issue #298), in one place, rather than buried inside
// each order's own edit form. Same "surface it, don't bury it" reasoning as
// the vision's own Backorder list. Each row names its order's type, links
// to that order, and shows who it shipped to. A shipment is never deleted
// (issue #292): Reverse takes its quantities back off the order and leaves
// it on the list, greyed, with who reversed it, when and why. Each shows
// its Invoice (issue #269), which it opens; Issue Invoice creates one for a
// shipment that isn't reversed and has none.
const FILTER_SCREEN_KEY = 'shipments';
// `customer` is the Ship To party's name, whatever the order type calls
// them — the key is kept so saved tabs carry on working.
const DEFAULT_FILTERS = { date_from: '', date_to: '', tracking_number: '', customer: '', order_number: '', order_type: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Same field-wrap look as LogActivityDrawer.js's own FilterDrawer body — a
// multi-field edit/create form belongs in the drawer, not an inline-
// expanding table row (ui-architecture.md's "Inline Row Expand vs. a
// Drawer": editing is different from read-only expansion, and a full form
// cramped into a growing/shrinking row makes the list jump around).
const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// Same "today, as a calendar date" shape the backend's own shipSalesOrder
// stamps fulfilled_date with — a shipment being added right now is almost
// always going out today, not blank.
// Seeded from the order_type/order_id/order_number query string when one's
// already active (arrived via a specific order's "View Shipments" Actions
// menu item) — the common case stays one click, the Order field pre-filled
// rather than making staff search for the order they just came from.
function blankNewShipment(orderType, orderId, orderNumber) {
  return {
    orderType: orderId ? orderType : '',
    orderId: orderId || '',
    orderLabel: orderNumber || '',
    orderSearch: '',
    orderResults: [],
    showOrderResults: false,
    ...blankShipFields(null, []),
    shipped_date: todayInTenant(),
  };
}

// A package's weight: its own, or else the packages directly inside it
// added up when every one was weighed. null when unknown.
function packageWeight(pkg) {
  if (pkg.weight !== null && pkg.weight !== undefined) return Number(pkg.weight);
  const inside = (pkg.packages || []).map(packageWeight);
  return inside.length && inside.every((w) => w !== null) ? inside.reduce((sum, w) => sum + w, 0) : null;
}

// A shipment's weight: its outermost packages' added up, when every one's
// is known (issue #313). null when there's nothing to show.
function shipmentWeight(row) {
  const weights = (row.packages || []).map(packageWeight);
  return weights.length && weights.every((w) => w !== null) ? weights.reduce((sum, w) => sum + w, 0) : null;
}

// "Satchel 3", "Carton 1 (2 inside)".
// A shipment's carrier cost (issue #350): its outermost packages' actual
// postage added up, what it charged for shipping beneath (its Invoice's
// once invoiced, else the order's), and a flag while
// any package's postage isn't entered yet.
function carrierCostCell(row) {
  const pkgs = row.packages || [];
  if (!pkgs.length) return '—';
  const postages = pkgs.map(actualPostageOf);
  const notEntered = postages.some((c) => c === null);
  return (
    <>
      {notEntered ? '—' : formatMoney(postages.reduce((sum, c) => sum + c, 0))}
      <div style={{ fontSize: 12, color: '#666' }}>Charged {shipmentShippingCharge(row) != null ? formatMoney(shipmentShippingCharge(row)) : '—'}</div>
      {notEntered && <div style={{ fontSize: 11, color: '#7a5c00' }}>Postage not entered</div>}
    </>
  );
}

function packagesLabel(row) {
  return (row.packages || []).map((p) => {
    const inside = insideSummary(p);
    return inside ? `${p.name} (${inside})` : p.name;
  }).join(', ');
}

// Who reversed a shipment, when, and why — the hover text on a reversed row.
function reversedLabel(row) {
  const who = row.reversed_by ? (row.reversed_by.name || row.reversed_by.email) : null;
  return [`Reversed ${formatDateTime(row.reversed_at)}`, who ? `by ${who}` : null, row.reversal_reason].filter(Boolean).join(' — ');
}

function ShipmentsTable() {
  const weightUnit = useShipmentWeightUnit();
  const { canCreate, canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderTypeParam = searchParams.get('order_type') || '';
  const orderId = orderTypeParam ? (searchParams.get('order_id') || '') : '';
  const orderNumber = searchParams.get('order_number') || '';
  // A shipped Package Label opened by staff (BoxScanPage.js) lands here on its
  // shipment: ?order_type&order_id narrow the list, ?shipment opens it.
  const shipmentParam = searchParams.get('shipment') || '';
  // The shipment whose Packing List is open (issues #296, #445).
  const [packingListShipmentId, setPackingListShipmentId] = useState(null);
  const { isStation } = usePackingStation();
  const [orderTypes, setOrderTypes] = useState([]);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  // Edit drawer / Reverse dialog — the shipment each is open on (null =
  // closed). See ShipmentEditDrawer.js and ShipmentReverseDialog.js.
  const [editingRow, setEditingRow] = useState(null);
  const [reversingRow, setReversingRow] = useState(null);

  // Add drawer — mutually exclusive with the edit drawer (see openAddForm/
  // startEdit below, each closes the other).
  const [showAddForm, setShowAddForm] = useState(false);
  const [newShipment, setNewShipment] = useState(() => blankNewShipment(orderTypeParam, orderId, orderNumber));
  const [addingShipment, setAddingShipment] = useState(false);
  const orderSearchTimer = useRef(null);

  // Whichever order is currently picked in the Add form (URL-provided or
  // searched), in the warehouse's shared order shape — fetched so the drawer
  // can show what's actually ready to ship (see computeShipRows above) and
  // Save can be blocked when nothing is. Kept independent of `showAddForm` so it's also ready in time for
  // the auto-preload effect below, which needs to know readiness before
  // deciding whether to open the drawer at all.
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const isPickedOrder = (detail) => !!detail && detail.order_type === newShipment.orderType && String(detail.id) === String(newShipment.orderId);
  useEffect(() => {
    if (!newShipment.orderId) { setOrderDetail(null); return; }
    if (isPickedOrder(orderDetail)) return;
    setOrderDetailLoading(true);
    axios.get(`/api/fulfilment/${newShipment.orderType}/${newShipment.orderId}`)
      .then((res) => setOrderDetail(res.data.data))
      .catch(() => setOrderDetail(null))
      .finally(() => setOrderDetailLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newShipment.orderType, newShipment.orderId]);
  // Seeds the new shipment's Carrier/Service/Options from the picked Order
  // (issue #281), and its packages that can ship (issues #299, #313) —
  // on open, on picking a different Order, or once that Order's detail
  // arrives; never on later edits to the fields themselves.
  useEffect(() => {
    if (!showAddForm || !isPickedOrder(orderDetail)) return;
    const seeded = blankShipFields(orderDetail, orderDetail.packages);
    delete seeded.shipped_date;
    delete seeded.tracking_number;
    setNewShipment((prev) => ({ ...prev, ...seeded }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail, newShipment.orderType, newShipment.orderId, showAddForm]);
  const canShipPackage = !!orderDetail && shippablePackages(orderDetail.packages).length > 0;

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/fulfilment/order-types')
      .then((res) => setOrderTypes(res.data.data || []))
      .catch(() => setOrderTypes([]));
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/shipments', {
        params: {
          search: search || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          tracking_number: filters.tracking_number || undefined,
          customer: filters.customer || undefined,
          order_number: filters.order_number || undefined,
          // One order's shipments (from its "View Shipments" link) wins
          // over the Order Type filter.
          order_type: orderId ? orderTypeParam : (filters.order_type || undefined),
          order_id: orderId || undefined,
          sortBy: 'shipped_date',
          sortOrder: 'DESC',
          page,
          limit,
        },
      });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters, orderTypeParam, orderId, page, limit]);

  useEffect(() => {
    if (!shipmentParam || !canUpdate) return;
    const row = rows.find((r) => String(r.id) === shipmentParam);
    if (!row || row.reversed_at) return;
    startEdit(row);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('shipment');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, shipmentParam]);

  const clearOrderFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('order_type');
      next.delete('order_id');
      next.delete('order_number');
      return next;
    });
    setPage(1);
  };

  const startEdit = (row) => {
    if (row.reversed_at) return;
    setShowAddForm(false);
    setEditingRow(row);
  };
  const cancelEdit = () => setEditingRow(null);
  const replaceRow = (updated) => setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));

  const openReverse = (row) => { cancelEdit(); setReversingRow(row); };

  const issueInvoice = async (row) => {
    if (!(await askConfirm(`Create an Invoice for Shipment ${row.display_number}, dated ${formatDate(row.shipped_date)}, for shipped items? An Invoice can't be edited once created.`, { confirmLabel: 'Create Invoice' }))) return;
    try {
      const res = await axios.post(`/api/shipments/${row.id}/invoice`);
      const invoice = res.data.data;
      replaceRow({ id: row.id, invoice: { id: invoice.id, display_number: invoice.display_number, voided_at: null }, can_invoice: false });
    } catch (err) {
      await showMessage(err.response?.data?.message || 'Failed to issue the Invoice', { title: 'Couldn’t Create Invoice' });
    }
  };

  const openAddForm = () => {
    cancelEdit();
    setNewShipment(blankNewShipment(orderTypeParam, orderId, orderNumber));
    setShowAddForm(true);
  };
  const closeAddForm = () => { setShowAddForm(false); setNewShipment(blankNewShipment(orderTypeParam, orderId, orderNumber)); };

  // Order search — a debounced input plus dropdown, across every order type (matches who the
  // order is for, or its number). Only needed when no order is already
  // fixed by the URL — see blankNewShipment above.
  const handleOrderSearchChange = (value) => {
    setNewShipment((prev) => ({ ...prev, orderSearch: value, orderType: '', orderId: '', orderLabel: '' }));
    clearTimeout(orderSearchTimer.current);
    if (!value.trim()) {
      setNewShipment((prev) => ({ ...prev, orderResults: [], showOrderResults: false }));
      return;
    }
    orderSearchTimer.current = setTimeout(() => {
      axios.get('/api/fulfilment/search', { params: { q: value } })
        .then((res) => setNewShipment((prev) => ({ ...prev, orderResults: res.data.data || [], showOrderResults: true })))
        .catch(() => setNewShipment((prev) => ({ ...prev, orderResults: [] })));
    }, 250);
  };
  const handleSelectOrder = (order) => {
    setNewShipment((prev) => ({
      ...prev,
      orderType: order.order_type,
      orderId: String(order.id),
      orderLabel: `${order.type_label} ${order.display_number} — ${order.party?.name || ''}`,
      orderSearch: '',
      showOrderResults: false,
    }));
  };

  // Adding a Shipment IS the Ship event now, not a separate record layered
  // on top — see fulfilmentService.ship, which ships the ticked packages
  // (issues #299, #313) and creates this record in one transaction. Save is
  // disabled below until a package is ticked — this guard is
  // belt-and-braces against a stale click.
  const handleAddShipment = async () => {
    if (!newShipment.orderId || !(newShipment.package_ids || []).length) return;
    setAddingShipment(true);
    try {
      await axios.post(`/api/fulfilment/${newShipment.orderType}/${newShipment.orderId}/ship`, shipPayload(newShipment));
      closeAddForm();
      fetchRows();
    } catch (err) {
      await showMessage(err.response?.data?.message || 'Failed to ship and record the shipment', { title: 'Couldn’t Ship' });
    }
    setAddingShipment(false);
  };

  // Shared by the Filters drawer and SavedFilterTabs' own "+ New" tab editor
  // (same field set serves both, so a saved tab and an ad hoc drawer edit
  // never drift apart — see ui-architecture.md's Single-Row Filters).
  const renderDrawerFields = (draft, setDraftField) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={FIELD_LABEL_STYLE}>Shipped from</label><br />
        <DateInput className="styled-input" value={draft.date_from} onChange={(e) => setDraftField('date_from', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Shipped to</label><br />
        <DateInput className="styled-input" value={draft.date_to} onChange={(e) => setDraftField('date_to', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Tracking #</label><br />
        <input className="styled-input" value={draft.tracking_number} onChange={(e) => setDraftField('tracking_number', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Order Type</label><br />
        <select className="styled-input" value={draft.order_type || ''} onChange={(e) => setDraftField('order_type', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {orderTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Ship To</label><br />
        <input className="styled-input" value={draft.customer} onChange={(e) => setDraftField('customer', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Order #</label><br />
        <input className="styled-input" value={draft.order_number} onChange={(e) => setDraftField('order_number', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
    </div>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Shipments</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              type="button"
              onClick={openAddForm}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}
            >
              <Plus size={15} /> Add Shipment
            </button>
          )}
        </div>
      </div>

      {orderId && (
        <FilterBanner
          message={(
            <>
              Showing shipments for {orderTypes.find((t) => t.key === orderTypeParam)?.label || 'Order'}{' '}
              <button
                type="button"
                onClick={() => { if (orderDetail?.link) navigate(orderDetail.link); }}
                style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                {orderNumber || orderId}
              </button>
            </>
          )}
          onClear={clearOrderFilter}
        />
      )}

      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Shipment #, Order #, tracking # or Ship To..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 290 }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Shipments list by date shipped, order type, or who it shipped to."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          {renderDrawerFields(filterDraft, setFilterDraftField)}
        </FilterDrawer>
      )}

      {/* Edit — one Shipment's fields, opened by clicking its row. */}
      {editingRow && (
        <ShipmentEditDrawer
          shipment={editingRow}
          onClose={cancelEdit}
          onSaved={(updated) => { replaceRow(updated); cancelEdit(); }}
          canPrintLabels={isStation}
        />
      )}

      {/* Add — same shell, plus an Order picker (search by who it's for or
          order #) when one isn't already fixed by the order_id filter (see
          blankNewShipment). */}
      {showAddForm && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeAddForm(); }}
          title="Add Shipment"
          description="Record a new shipment against an order."
          footer={(
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <CancelButton onClick={closeAddForm} disabled={addingShipment} />
              <SaveButton
                onClick={handleAddShipment}
                loading={addingShipment}
                disabled={!newShipment.orderId || !(newShipment.package_ids || []).length || orderDetailLoading || !!orderDetail?.ship_blocked}

                type="button"
              />
            </div>
          )}
        >
          <div style={fieldWrapStyle}>
            <label style={fieldLabelStyle}>Order</label>
            {newShipment.orderId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 34, padding: '0 8px', border: '1px solid #bfc8d6', borderRadius: 6, fontSize: 14, background: '#f7f9fc' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={newShipment.orderLabel}>{newShipment.orderLabel}</span>
                {!orderId && (
                  <button type="button" onClick={() => setNewShipment((prev) => ({ ...prev, orderType: '', orderId: '', orderLabel: '' }))} title="Change Order" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#555' }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  className="styled-input"
                  placeholder="Search ship to or order #..."
                  value={newShipment.orderSearch}
                  onChange={(e) => handleOrderSearchChange(e.target.value)}
                  onFocus={() => { if (newShipment.orderResults.length) setNewShipment((prev) => ({ ...prev, showOrderResults: true })); }}
                  onBlur={() => setTimeout(() => setNewShipment((prev) => ({ ...prev, showOrderResults: false })), 150)}
                  style={{ width: '100%', fontSize: 14 }}
                />
                {newShipment.showOrderResults && newShipment.orderResults.length > 0 && (
                  <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                    {newShipment.orderResults.map((o) => (
                      <li
                        key={`${o.order_type}-${o.id}`}
                        onMouseDown={() => handleSelectOrder(o)}
                        style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {o.type_label} {o.display_number} — {o.party?.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Ready to Ship — the actual connection between "add a shipment"
              and the Order's own Picked/Packed quantities (vis_order_
              fulfilment.md): Save performs the real Ship pass (moving
              qty_shipped for whatever's Packed-but-not-Shipped) in the same
              transaction as recording this tracking info, via
              fulfilmentService.ship. Nothing ready means there's nothing to ship,
              so the rest of the form is withheld entirely rather than just
              disabling Save — filling in a tracking # for a shipment that
              can't be created reads as broken, not blocked. */}
          {newShipment.orderId && (
            orderDetailLoading ? (
              <div style={{ fontSize: 13, color: '#888', margin: '4px 0 14px' }}>Loading Order...</div>
            ) : !canShipPackage ? (
              <div style={{ fontSize: 13, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '8px 12px', margin: '4px 0 14px' }}>
                Nothing ready to ship on this order — it has no Packed quantity that isn't already Shipped. Pick and pack it first.
              </div>
            ) : orderDetail.ship_blocked ? (
              <div style={{ fontSize: 13, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', margin: '4px 0 14px' }}>
                {orderDetail.ship_blocked.message}
              </div>
            ) : (
              <ShipConfirmFields
                lines={orderDetail?.lines}
                packages={orderDetail?.packages}
                value={newShipment}
                onChange={(next) => setNewShipment((prev) => ({ ...prev, ...next }))}
                orderDate={orderDetail?.order_date}
              />
            )
          )}
        </FilterDrawer>
      )}

      {reversingRow && (
        <ShipmentReverseDialog
          shipment={reversingRow}
          onClose={() => setReversingRow(null)}
          onReversed={(updated) => { replaceRow(updated); setReversingRow(null); }}
        />
      )}

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <colgroup>
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '6%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Date Shipped</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Shipment #</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Order Type</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Order #</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Ship To</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Carrier / Service</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Tracking #</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Packages</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>{weightUnit ? `Weight (${weightUnit})` : 'Weight'}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }} title="The actual postage paid for this shipment, ex GST, beside the shipping the Customer was charged on the order">Carrier Cost</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No shipments</td></tr>
            ) : rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  className="list-row"
                  onClick={() => { if (canUpdate && !row.reversed_at) startEdit(row); }}
                  style={{ cursor: canUpdate && !row.reversed_at ? 'pointer' : 'default', color: row.reversed_at ? '#8a94a6' : undefined }}
                  title={!row.reversed_at && canUpdate ? 'Click to edit' : undefined}
                >
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ textDecoration: row.reversed_at ? 'line-through' : 'none' }}>{row.shipped_date ? formatDate(row.shipped_date) : '—'}</span>
                    {row.reversed_at && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: 0.3 }}>Reversed</div>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                    {row.display_number || '—'}
                    {row.invoice && (
                      <div style={{ fontSize: 12, fontWeight: 400 }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => navigate(`/receivables-invoices/${row.invoice.id}`)}
                          title="Open the Invoice"
                          style={{ background: 'none', border: 'none', padding: 0, color: row.invoice.voided_at ? '#8a94a6' : '#1976d2', textDecoration: row.invoice.voided_at ? 'line-through' : 'underline', cursor: 'pointer', fontSize: 12 }}
                        >
                          {row.invoice.display_number}
                        </button>
                        {row.invoice.voided_at && <span style={{ marginLeft: 4, color: '#8a94a6' }}>Void</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{row.order?.type_label || '—'}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }} onClick={(e) => e.stopPropagation()}>
                    {row.order ? (
                      <button
                        type="button"
                        onClick={() => navigate(row.order.link)}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 14 }}
                      >
                        {row.order.display_number}
                      </button>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{row.order?.party?.name || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {carriageLabel(row) || '—'}
                    {row.shipping_options?.length > 0 && (
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {row.shipping_options.map((o) => o.shipping_option?.description).filter(Boolean).join(', ')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px' }} title={row.tracking_number || undefined}>{row.tracking_number || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{packagesLabel(row) || '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{shipmentWeight(row) !== null ? formatNumber(shipmentWeight(row), { decimals: 1 }) : '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{carrierCostCell(row)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {!row.reversed_at && (
                      <RowActionsMenu
                        items={[
                          { key: 'packing-list', label: 'Packing List', onClick: () => setPackingListShipmentId(row.id) },
                          ...(canUpdate ? [
                            { key: 'edit', label: 'Edit', onClick: () => startEdit(row) },
                            ...(row.can_invoice ? [{ key: 'invoice', label: 'Issue Invoice', onClick: () => issueInvoice(row) }] : []),
                            { key: 'reverse', label: 'Reverse', onClick: () => openReverse(row), danger: true },
                          ] : []),
                        ]}
                      />
                    )}
                  </td>
                </tr>
                {row.reversed_at && (
                  <tr>
                    <td colSpan={11} style={{ padding: '0 8px 8px', fontSize: 12, color: '#8a94a6', borderBottom: '1px solid #eceef1' }}>
                      {reversedLabel(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {packingListShipmentId && (
        <ReportOverlay onClose={() => setPackingListShipmentId(null)}>
          <PackingListReportView shipmentId={packingListShipmentId} onClose={() => setPackingListShipmentId(null)} />
        </ReportOverlay>
      )}

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Shipments"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default ShipmentsTable;
